import {
  createContext,
  use,
  useEffect,
  useId,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { SideSheetStackStore } from "./types";

const SideSheetStackContext = createContext<SideSheetStackStore | null>(null);

function createSheetStore(): SideSheetStackStore {
  const listeners = new Set<() => void>();
  const order: string[] = [];

  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getDepth: () => order.length,
    register: (id) => {
      if (!order.includes(id)) {
        order.push(id);
        notify();
      }
    },
    unregister: (id) => {
      const idx = order.indexOf(id);
      if (idx !== -1) {
        order.splice(idx, 1);
        notify();
      }
    },
    getIndex: (id) => order.indexOf(id),
  };
}

export function SideSheetStackProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => createSheetStore(), []);

  return (
    <SideSheetStackContext value={store}>{children}</SideSheetStackContext>
  );
}

function useStore(): SideSheetStackStore {
  const store = use(SideSheetStackContext);
  if (!store) {
    throw new Error(
      "useSideSheetStack must be used within a SideSheetStackProvider",
    );
  }
  return store;
}

export function useSideSheetRegistration(open: boolean) {
  const store = useStore();
  const id = useId();

  useEffect(() => {
    if (!open) return;
    store.register(id);
    return () => store.unregister(id);
  }, [open, store, id]);

  const depth = useSyncExternalStore(store.subscribe, store.getDepth);
  const myIndex = useSyncExternalStore(store.subscribe, () =>
    store.getIndex(id),
  );

  return { depth, myIndex };
}
